"use strict";
/**
 * 高精度浮點數運算工具庫
 * 解決 JavaScript 浮點數運算精度問題
 *
 * 使用方法：
 * import { add, subtract, multiply, divide } from './precisionMath'
 *
 * const result = add(0.1, 0.2) // 0.3 而不是 0.30000000000000004
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.percentage = exports.average = exports.sum = exports.lessThan = exports.greaterThan = exports.equals = exports.round = exports.divide = exports.multiply = exports.subtract = exports.add = void 0;
/**
 * 獲取數字的小數位數
 */
function getDecimalLength(num) {
    const str = num.toString();
    const decimalIndex = str.indexOf('.');
    if (decimalIndex === -1)
        return 0;
    // 處理科學記數法
    const eIndex = str.toLowerCase().indexOf('e');
    if (eIndex > -1) {
        const base = str.slice(decimalIndex + 1, eIndex).length;
        const exponent = parseInt(str.slice(eIndex + 1));
        return Math.max(0, base - exponent);
    }
    return str.length - decimalIndex - 1;
}
/**
 * 將數字轉換為整數（通過乘以 10 的 n 次方）
 */
function toInteger(num) {
    const decimalLength = getDecimalLength(num);
    const power = Math.pow(10, decimalLength);
    return {
        value: Math.round(num * power),
        power: decimalLength
    };
}
/**
 * 高精度加法
 * @param a 加數
 * @param b 加數
 * @param precision 結果保留的小數位數，默認為 12
 */
function add(a, b, precision = 12) {
    if (a === 0)
        return round(b, precision);
    if (b === 0)
        return round(a, precision);
    const aInfo = toInteger(a);
    const bInfo = toInteger(b);
    const maxPower = Math.max(aInfo.power, bInfo.power);
    const multiplier = Math.pow(10, maxPower);
    const aInt = Math.round(a * multiplier);
    const bInt = Math.round(b * multiplier);
    return round((aInt + bInt) / multiplier, precision);
}
exports.add = add;
/**
 * 高精度減法
 * @param a 被減數
 * @param b 減數
 * @param precision 結果保留的小數位數，默認為 12
 */
function subtract(a, b, precision = 12) {
    if (b === 0)
        return round(a, precision);
    const aInfo = toInteger(a);
    const bInfo = toInteger(b);
    const maxPower = Math.max(aInfo.power, bInfo.power);
    const multiplier = Math.pow(10, maxPower);
    const aInt = Math.round(a * multiplier);
    const bInt = Math.round(b * multiplier);
    return round((aInt - bInt) / multiplier, precision);
}
exports.subtract = subtract;
/**
 * 高精度乘法
 * @param a 乘數
 * @param b 乘數
 * @param precision 結果保留的小數位數，默認為 12
 */
function multiply(a, b, precision = 12) {
    if (a === 0 || b === 0)
        return 0;
    const aInfo = toInteger(a);
    const bInfo = toInteger(b);
    const result = (aInfo.value * bInfo.value) / Math.pow(10, aInfo.power + bInfo.power);
    return round(result, precision);
}
exports.multiply = multiply;
/**
 * 高精度除法
 * @param a 被除數
 * @param b 除數
 * @param precision 結果保留的小數位數，默認為 12
 */
function divide(a, b, precision = 12) {
    if (b === 0) {
        console.warn('precisionMath.divide: Division by zero');
        return 0;
    }
    if (a === 0)
        return 0;
    const aInfo = toInteger(a);
    const bInfo = toInteger(b);
    const result = (aInfo.value / bInfo.value) * Math.pow(10, bInfo.power - aInfo.power);
    return round(result, precision);
}
exports.divide = divide;
/**
 * 高精度四捨五入
 * @param num 要四捨五入的數字
 * @param precision 保留的小數位數，默認為 10
 */
function round(num, precision = 10) {
    if (precision < 0)
        precision = 0;
    if (precision > 15)
        precision = 15; // JavaScript 最大安全精度
    const multiplier = Math.pow(10, precision);
    return Math.round(num * multiplier) / multiplier;
}
exports.round = round;
/**
 * 高精度比較：判斷 a 是否等於 b
 * @param a 數字 a
 * @param b 數字 b
 * @param tolerance 容差，默認為 1e-10
 */
function equals(a, b, tolerance = 1e-10) {
    return Math.abs(a - b) < tolerance;
}
exports.equals = equals;
/**
 * 高精度比較：判斷 a 是否大於 b
 * @param a 數字 a
 * @param b 數字 b
 * @param tolerance 容差，默認為 1e-10
 */
function greaterThan(a, b, tolerance = 1e-10) {
    return a - b > tolerance;
}
exports.greaterThan = greaterThan;
/**
 * 高精度比較：判斷 a 是否小於 b
 * @param a 數字 a
 * @param b 數字 b
 * @param tolerance 容差，默認為 1e-10
 */
function lessThan(a, b, tolerance = 1e-10) {
    return b - a > tolerance;
}
exports.lessThan = lessThan;
/**
 * 批量加法
 * @param numbers 要相加的數字陣列
 * @param precision 結果保留的小數位數，默認為 12
 */
function sum(numbers, precision = 12) {
    return numbers.reduce((acc, num) => add(acc, num, precision + 2), 0);
}
exports.sum = sum;
/**
 * 計算平均值
 * @param numbers 數字陣列
 * @param precision 結果保留的小數位數，默認為 12
 */
function average(numbers, precision = 12) {
    if (numbers.length === 0)
        return 0;
    return divide(sum(numbers, precision + 2), numbers.length, precision);
}
exports.average = average;
/**
 * 計算百分比
 * @param value 數值
 * @param percentage 百分比（例如 5 代表 5%）
 * @param precision 結果保留的小數位數，默認為 12
 */
function percentage(value, percentage, precision = 12) {
    return multiply(value, divide(percentage, 100, precision + 2), precision);
}
exports.percentage = percentage;
// 默認導出
exports.default = {
    add,
    subtract,
    multiply,
    divide,
    round,
    equals,
    greaterThan,
    lessThan,
    sum,
    average,
    percentage,
};
//# sourceMappingURL=precisionMath.js.map